import { layout, dia, util } from '@clientio/rappid'
import * as dagre from 'dagre'
import RappidContext from './rappid-context'
import { useHelpers } from '../composables/helpers'
import _ from 'lodash'

export class GraphLayout {
  constructor(private context: RappidContext) {}

  layout(): void {
    this.directedGraphLayout()
  }

  private directedGraphLayout(): void {
    this.context.paper.freeze()

    const { getArtifactTypeFromCellView } = useHelpers()

    this.context.paper.model.getElements().forEach((element) => {
      const elementView = element.findView(this.context.paper)
      const currentAT = getArtifactTypeFromCellView(elementView)
      if (
        currentAT !== undefined &&
        !currentAT.collapsed &&
        !element.get('isPill') &&
        !element.get('collapsed')
      ) {
        if (
          element.getEmbeddedCells().length > 0 &&
          !element.getEmbeddedCells().every((embeddedCell) => embeddedCell.get('isPill'))
        ) {
          const children = element
            .getEmbeddedCells()
            .filter((c) => c.get('hidden') === undefined || c.get('hidden') === false)
          layout.GridLayout.layout(children, {
            marginX: 0,
            marginY: 0,
            rowGap: 15,
            horizontalAlign: 'middle',
            verticalAlign: 'middle',
          })
          const bbox = this.context.graph.getCellsBBox(children)
          if (bbox) {
            element.position(bbox.x - 10, bbox.y - 90)
            element.resize(bbox.width + 20, bbox.height + 110)
          }
        } else if (
          element.getEmbeddedCells().length === 1 &&
          element.getEmbeddedCells().every((embeddedCell) => embeddedCell.get('isPill'))
        ) {
          const bbox = this.context.paper.model.getCellsBBox(element.getEmbeddedCells())
          layout.GridLayout.layout(element.getEmbeddedCells(), {
            marginX: bbox?.x,
            marginY: bbox?.y,
            rowGap: 15,
            horizontalAlign: 'middle',
            verticalAlign: 'middle',
          })
          element.fitToChildren({ padding: { top: 90, bottom: 20, left: 160, right: 160 } })
        } else {
          element.resize(420, 100)
        }
      }
    })

    // create temporary graph
    const tempGraph = new dia.Graph()
    const containers = this.context.graph
      .getElements()
      .filter((element) => !element.getParentCell())
    tempGraph.addCells(containers)
    containers.forEach((container) => {
      const neighbors = util.uniq(
        this.context.graph
          .getNeighbors(container, { deep: true })
          .map((el) => el.getParentCell() || el),
      )
      const links = neighbors.map((neighbor) => {
        return new dia.Link({
          source: { id: container.id },
          target: { id: neighbor.id },
        })
      })
      tempGraph.addCells(links)
    })

    // Be aware of this open layouting issue: https://github.com/clientIO/joint/issues/455
    // Because of this issue, artifact type containers with links to other elements AND embedded artifacts that also
    // have links must not be present when layouting is run.
    layout.DirectedGraph.layout(tempGraph, {
      dagre,
      graphlib: dagre.graphlib,
      setVertices: false,
      setLabels: true,
      setPosition(element, glNode) {
        element.position(glNode.x - glNode.width / 2, glNode.y - glNode.height / 2, {
          deep: true,
        })
      },
      ranker: 'tight-tree',
      rankDir: 'LR',
      align: 'UL',
      resizeClusters: false,
      rankSep: 100,
      edgeSep: 40,
      nodeSep: 80,
    })

    tempGraph.getLinks().forEach((link) => {
      let dx: number
      const sourceElement = link.getSourceElement()
      const targetElement = link.getTargetElement()
      if (sourceElement && targetElement) {
        dx = sourceElement.position().x - targetElement.position().x
        if (dx > 0 || dx < -520) {
          link.remove()
        }
      }
    })

    layout.DirectedGraph.layout(tempGraph, {
      dagre,
      graphlib: dagre.graphlib,
      setVertices: false,
      setLabels: true,
      setPosition(element, glNode) {
        element.position(glNode.x - glNode.width / 2, glNode.y - glNode.height / 2, {
          deep: true,
        })
      },
      ranker: 'tight-tree',
      rankDir: 'LR',
      align: 'UL',
      resizeClusters: false,
      rankSep: 100,
      edgeSep: 40,
      nodeSep: 80,
    })

    const artifactTypeElements = this.context.graph
      .getElements()
      .filter(
        (e: dia.Element) =>
          e.get('artifactType') !== undefined &&
          e.get('isPill') === undefined &&
          (e.get('hidden') === undefined || e.get('hidden') === false),
      )

    const elementsByX = _.groupBy(artifactTypeElements, (e: dia.Element) => e.position().x)

    const bboxByX = Object.keys(elementsByX).reduce((acc: Record<string, joint.g.Rect>, curr) => {
      const val = this.context.graph.getCellsBBox(elementsByX[curr])
      if (val !== null) {
        acc[curr] = val
      }
      return acc
    }, {})

    const getLocalMaxY = (from: number, to: number) => {
      ;[from, to] = [from, to].sort((a, b) => a - b)
      const keys = Object.keys(bboxByX).filter((x) => parseInt(x) > from && parseInt(x) < to - 10)
      const bboxes = keys.map((x) => bboxByX[x])
      const max = bboxes.reduce((acc, bbox) => Math.max(acc, bbox.y + bbox.height + 20), -Infinity)
      return max
    }
    const getLocalMinY = (from: number, to: number) => {
      ;[from, to] = [from, to].sort((a, b) => a - b)
      const keys = Object.keys(bboxByX).filter((x) => parseInt(x) > from && parseInt(x) < to - 10)
      const bboxes = keys.map((x) => bboxByX[x])
      const min = bboxes.reduce((acc, bbox) => Math.min(acc, bbox.y - 20), Infinity)
      return min
    }

    //just visible links
    const visibleLinks = this.context.graph
      .getLinks()
      .filter((l) => l.get('hidden') === undefined || l.get('hidden') === false)

    visibleLinks.forEach((link) => {
      const sourceElement = link.getSourceElement()
      const targetElement = link.getTargetElement()
      const sourceContainer = sourceElement?.getParentCell() || sourceElement
      const targetContainer = targetElement?.getParentCell() || targetElement

      // positive number => left to right, negative right to left
      let distance: number
      if (targetElement && sourceElement) {
        distance = targetElement?.position().x - sourceElement?.position().x
      } else {
        distance = 520
      }
      if (link.attributes.linkType && sourceContainer === targetContainer) {
        link.prop('source/anchor', { name: 'left' }, { rewrite: true })
        link.prop('target/anchor', { name: 'top' }, { rewrite: true })
        link.prop('connector/args/cornerRadius', 1)
        link.router('rightAngle')
      } else if (sourceElement === targetElement) {
        link.prop('source/anchor', { name: 'left' }, { rewrite: true })
        link.prop('target/anchor', { name: 'top' }, { rewrite: true })
        link.prop('connector/args/cornerRadius', 1)
        link.router('rightAngle')
      } else if (sourceContainer === targetContainer) {
        link.prop('source/anchor', { name: 'left' }, { rewrite: true })
        link.prop('target/anchor', { name: 'left' }, { rewrite: true })
        link.router('rightAngle')
      } else if (sourceElement && targetElement && distance < -550) {
        const maxY = getLocalMaxY(sourceElement?.position().x, targetElement?.position().x)
        const minY = getLocalMinY(sourceElement?.position().x, targetElement?.position().x)
        link.prop('source/anchor', { name: 'left' }, { rewrite: true })
        link.prop('target/anchor', { name: 'right' }, { rewrite: true })
        link.router('rightToLeft', {
          maxY: maxY,
          minY: minY,
        })
      } else if (distance < 0) {
        // right to left small distance
        link.prop('source/anchor', { name: 'left' }, { rewrite: true })
        link.prop('target/anchor', { name: 'right' }, { rewrite: true })
        link.router('rightAngle')
      } else if (sourceElement && targetElement && distance > 550) {
        const maxY = getLocalMaxY(sourceElement?.position().x, targetElement?.position().x)
        const minY = getLocalMinY(sourceElement?.position().x, targetElement?.position().x)
        link.prop('source/anchor', { name: 'right' }, { rewrite: true })
        link.prop('target/anchor', { name: 'left' }, { rewrite: true })
        link.router('leftToRight', {
          maxY: maxY,
          minY: minY,
        })
      } else {
        link.prop('source/anchor', { name: 'right' }, { rewrite: true })
        link.prop('target/anchor', { name: 'left' }, { rewrite: true })
        link.router('rightAngle')
      }
    })

    this.context.paper.unfreeze()
  }
}
